import { Lock } from 'lucide-react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AnySpec, KindMap } from 'v5/core/form-schema/formKinds.types';
import type { EntitySchema } from 'v5/core/form-schema/formSchema.types';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from 'v5/design-system/Command';
import {
  buildFieldJumpEntries,
  type FieldJumpResult,
  searchFieldJump
} from './fieldJump';
import type { FormKit, UnitBindings, UnitTarget } from './schemaForm.types';

export interface FieldJumpDialogProps<M extends KindMap<M>> {
  open: boolean;
  onOpenChange(open: boolean): void;
  /** The form whose fields it finds, with the values as they are when it opens. */
  schema: EntitySchema<AnySpec<M>>;
  kit: FormKit<M>;
  bindings: UnitBindings<M>;
  /** Where the picked field leads; called once the dialog has closed and let go of the focus. */
  onPick(target: UnitTarget): void;
}

/**
 * Finds a field of the form by its name or by a value it holds, in the
 * command bar's look: sections as groups, the value under each name, a lock
 * on fields that can't be edited. Enter picks the highlighted one.
 */
export function FieldJumpDialog<M extends KindMap<M>>({
  open,
  onOpenChange,
  onPick,
  ...form
}: FieldJumpDialogProps<M>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SchemaForm.jump' });
  const pickedRef = useRef<UnitTarget | null>(null);
  // No trigger opens it (a hotkey, the command bar): what had the focus
  // gets it back when it closes without a pick. Read before the dialog
  // moves the focus inside.
  const returnFocusRef = useRef<Element | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    pickedRef.current = null;
    returnFocusRef.current = document.activeElement;
  }, [open]);
  return (
    <CommandDialog
      label={t('title')}
      open={open}
      onOpenChange={onOpenChange}
      onCloseAutoFocus={event => {
        const picked = pickedRef.current;
        const returnFocus = returnFocusRef.current;
        pickedRef.current = null;
        returnFocusRef.current = null;
        if (picked !== null) {
          // The picked field takes the focus, not what held it before.
          event.preventDefault();
          onPick(picked);
          return;
        }
        if (returnFocus instanceof HTMLElement && returnFocus.isConnected) {
          event.preventDefault();
          returnFocus.focus({ preventScroll: true });
        }
      }}
    >
      <FieldJumpSearch
        {...form}
        onPick={target => {
          pickedRef.current = target;
          onOpenChange(false);
        }}
      />
    </CommandDialog>
  );
}

/**
 * Mounted while the dialog is open, closing animation included: every
 * opening starts with an empty query over the values of that moment, which
 * stay on screen as it fades out.
 */
function FieldJumpSearch<M extends KindMap<M>>({
  schema,
  kit,
  bindings,
  onPick
}: Pick<FieldJumpDialogProps<M>, 'schema' | 'kit' | 'bindings' | 'onPick'>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SchemaForm.jump' });
  const [entries] = useState(() =>
    buildFieldJumpEntries(schema, bindings, kit)
  );
  const [query, setQuery] = useState('');
  const groups = useMemo(
    () => searchFieldJump(entries, query),
    [entries, query]
  );
  const showSections = new Set(entries.map(entry => entry.section.id)).size > 1;
  return (
    <>
      <CommandInput
        placeholder={t('placeholder')}
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>{t('empty')}</CommandEmpty>
        {groups.map(group => (
          <CommandGroup
            key={group.section.id}
            heading={showSections ? group.section.title : undefined}
          >
            {group.results.map(result => (
              <CommandItem
                key={result.entry.key}
                value={result.entry.key}
                onSelect={() => onPick(result.target)}
              >
                <FieldJumpItem result={result} readOnlyLabel={t('readOnly')} />
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </>
  );
}

function FieldJumpItem({
  result: { entry, valueText },
  readOnlyLabel
}: {
  result: FieldJumpResult;
  readOnlyLabel: string;
}) {
  const { Icon } = entry;
  return (
    <>
      <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border-default bg-bg-default text-fg-muted">
        {Icon !== null && <Icon className="size-4" />}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">
          {entry.unitTitle !== null && (
            <span className="text-fg-muted">{entry.unitTitle} › </span>
          )}
          {entry.label}
        </span>
        {valueText !== null && (
          <span className="truncate text-sm text-fg-muted">{valueText}</span>
        )}
      </div>
      {!entry.editable && (
        <span className="flex shrink-0 items-center gap-1 text-sm text-fg-muted">
          <Lock className="size-4" aria-hidden />
          <span className="sr-only">{readOnlyLabel}</span>
        </span>
      )}
    </>
  );
}
